import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Premium Button Component - Swiss Luxury Aesthetic
 * Follows design system with precise spacing and refined interactions
 */
const Button = React.forwardRef(({
  children,
  className,
  variant = 'primary',
  size = 'default',
  asChild = false,
  ...props
}, ref) => {
  const Component = asChild ? React.Fragment : 'button';

  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium tracking-tight transition-all duration-200 ease-out hover:scale-[1.02] active:scale-[0.98]';
  // Solid teal outline from the global :focus-visible rule (styles/index.css)
  const focusStyles = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]';
  const disabledStyles = 'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  const variants = {
    primary: 'bg-[var(--accent)] text-white shadow-[0_14px_28px_rgba(15,23,42,0.14)] hover:bg-[#183c42] hover:shadow-[0_18px_36px_rgba(15,23,42,0.18)] active:bg-[#132f33]',
    secondary: 'bg-[var(--accent-light)] text-[var(--accent)] border border-[rgba(31,76,84,0.18)] shadow-[0_10px_24px_rgba(15,23,42,0.08)] hover:bg-white hover:text-[var(--accent)]',
    muted: 'bg-white text-[var(--text-primary)] border border-[rgba(31,76,84,0.12)] hover:bg-[var(--accent-light)]',
    outline: 'bg-transparent text-[var(--text-primary)] border border-[rgba(27,36,48,0.2)] hover:bg-[rgba(31,76,84,0.08)]',
    ghost: 'bg-transparent text-[var(--accent)] hover:bg-[rgba(31,76,84,0.08)]',
    danger: 'bg-[#9f2b2b] text-white shadow-[0_10px_20px_rgba(159,43,43,0.25)] hover:bg-[#862121]',
    link: 'bg-transparent text-[var(--accent)] underline-offset-4 hover:underline'
  };

  const sizes = {
    sm: 'h-9 px-4 text-sm rounded-full',
    default: 'h-11 px-6 text-base rounded-full',
    lg: 'h-12 px-8 text-base rounded-full',
    xl: 'h-14 px-10 text-lg rounded-full',
    icon: 'h-11 w-11 rounded-full',
  };

  return (
    <Component
      ref={ref}
      className={cn(
        baseStyles,
        focusStyles,
        disabledStyles,
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});

Button.displayName = 'Button';

export { Button };
